import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem
} from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as PermisosIcon
} from '@mui/icons-material';
import { useUsuarioStore } from '../store/usuarioStore';
import { rolService } from '../services/rolService';
import type { Rol } from '../types/usuario.types';
import { initialRolState } from '../types/usuario.types';
import AsignarPermisosDialog from './AsignarPermisosDialog';

export const RolesPage = () => {
  const { roles, setRoles, setLoading, removeRol, addRol, updateRol } = useUsuarioStore();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRol, setSelectedRol] = useState<Rol>(initialRolState);
  const [openPermisosDialog, setOpenPermisosDialog] = useState(false);
  const [selectedRolForPermisos, setSelectedRolForPermisos] = useState<Rol | undefined>();

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await rolService.getAll();
      setRoles(data);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setSelectedRol(initialRolState);
    setOpenDialog(true);
  };

  const handleEdit = (rol: Rol) => {
    setSelectedRol(rol);
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar este rol?')) {
      try {
        await rolService.delete(id);
        removeRol(id);
      } catch (error: any) {
        alert(error.response?.data || 'Error al eliminar rol');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const saved = await rolService.save(selectedRol);

      if (selectedRol.id === 0) {
        addRol(saved);
      } else {
        updateRol(saved);
      }

      setOpenDialog(false);
      setSelectedRol(initialRolState);
    } catch (error: any) {
      alert(error.response?.data || 'Error al guardar rol');
    }
  };

  const handleAsignarPermisos = (rol: Rol) => {
    setSelectedRolForPermisos(rol);
    setOpenPermisosDialog(true);
  };

  const handleClosePermisosDialog = () => {
    setOpenPermisosDialog(false);
    setSelectedRolForPermisos(undefined);
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'codigo', headerName: 'Código', flex: 1 },
    { field: 'descripcion', headerName: 'Descripción', flex: 2 },
    {
      field: 'esObligatorio',
      headerName: 'Obligatorio',
      width: 120,
      type: 'boolean'
    },
    {
      field: 'esOculto',
      headerName: 'Oculto',
      width: 100,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 200,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<PermisosIcon />}
          label="Asignar Permisos"
          onClick={() => handleAsignarPermisos(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Editar"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Eliminar"
          onClick={() => handleDelete(params.row.id)}
          disabled={params.row.esObligatorio}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">Gestión de Roles</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNew}
          >
            Nuevo Rol
          </Button>
        </Box>

        <DataGrid
          rows={roles}
          columns={columns}
          autoHeight
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
        />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedRol.id === 0 ? 'Nuevo Rol' : 'Editar Rol'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Código"
                value={selectedRol.codigo}
                onChange={(e) => setSelectedRol({ ...selectedRol, codigo: e.target.value })}
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descripción"
                value={selectedRol.descripcion}
                onChange={(e) => setSelectedRol({ ...selectedRol, descripcion: e.target.value })}
                required
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedRol.esObligatorio}
                    onChange={(e) => setSelectedRol({ ...selectedRol, esObligatorio: e.target.checked })}
                  />
                }
                label="Obligatorio"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedRol.esOculto}
                    onChange={(e) => setSelectedRol({ ...selectedRol, esOculto: e.target.checked })}
                  />
                }
                label="Oculto"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedRol.id === 0 ? 'Crear' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <AsignarPermisosDialog
        open={openPermisosDialog}
        onClose={handleClosePermisosDialog}
        rol={selectedRolForPermisos}
      />
    </Box>
  );
};

export default RolesPage;
